import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  createSignature,
  removeSignature,
  saveSignature,
  signaturesQuery,
  type SignatureTemplate,
  type SignatureVariable,
  type UsersShell,
} from '../../api/settingsUsers'
import { Forbidden } from '../../ui/Forbidden'
import { Modal } from '../../ui/Modal'
import { Spinner } from '../../ui/Spinner'
import { toast } from '../../ui/notify'
import { failed, whenText } from './shared'

/**
 * Signatures - `signature/index.php` (cadru + doua modale), `signature/list.php`
 * (lista), `signature/addForm.php` si `signature/editForm.php`.
 *
 * -----------------------------------------------------------------------------
 * CE E ALTFEL, SI DE CE
 * -----------------------------------------------------------------------------
 *
 * CORPUL SEMNATURII se scrie intr-o zona de text simpla, cu HTML in ea, si se
 * vede alaturi asa cum va arata. In portal era CKEditor 4, cu bara lui de
 * unelte si cu incarcare de imagini prin `upload_template_image()` - un capat
 * care raspundea cu un `<script>` ce chema `CKEDITOR.tools.callFunction(1, ...)`
 * in fereastra parinte. Protocolul ala e al editorului vechi; v4 nu-l are, deci
 * incarcarea de imagini din editor NU s-a mutat. Imaginile se leaga din afara,
 * cu `<img src="...">`.
 *
 * LISTA DE VARIABILE ramane: se apasa pe una si se lipeste la cursor, ca in
 * `addForm-1.js`.
 */
export function SignaturesPanel({ shell }: { shell: UsersShell }) {
  const client = useQueryClient()
  const page = useQuery(signaturesQuery)
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<SignatureTemplate | null>(null)
  const [preview, setPreview] = useState<SignatureTemplate | null>(null)

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const reload = () => void client.invalidateQueries({ queryKey: ['settings', 'users', 'signatures'] })

  const drop = (template: SignatureTemplate) => {
    if (!confirm('Remove this signature template?')) return
    void removeSignature(template.id)
      .then(() => {
        toast('Signature removed', 'Success')
        reload()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  return (
    <div id="content">
      <h4>Users signatures</h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit user <strong>signature</strong>
      </label>
      {shell.may['settings.user_accounts.edit'] ? (
        <button className="btn btn-success pull-right" type="button" onClick={() => setAdding(true)}>
          <i className="fas fa-plus" aria-hidden="true"></i> New user signature
        </button>
      ) : null}
      <hr />

      <div className="list-group">
        {page.data.templates.map((template) => (
          <section className="settings" key={template.id}>
            <div className="row">
              <div className="col-md-2">
                <h5>
                  <i className="far fa-address-book" aria-hidden="true"></i> {template.name}
                </h5>
                <p className="color-green3" style={{ marginTop: 5 }}>
                  <i className="fas fa-circle" aria-hidden="true"></i> Active
                </p>
              </div>
              <div className="col-md-6" style={{ wordWrap: 'break-word' }}>
                <div>{plainPreview(template.body)}</div>
                <a
                  href="#preview"
                  className="preview-template color-blue"
                  onClick={(event) => {
                    event.preventDefault()
                    setPreview(template)
                  }}
                >
                  <i className="fas fa-search-plus" aria-hidden="true"></i> Preview signature body
                </a>
              </div>
              <div className="col-md-2">
                <span className="updated">{whenText(template.updated)}</span>
                <p className="color-green3">
                  <i className="far fa-clock" aria-hidden="true"></i> Updated
                </p>
              </div>
              <div className="col-md-2 aright">
                <button className="btn btn-xs btn-primary" type="button" onClick={() => setEditing(template)}>
                  Edit
                </button>{' '}
                <button className="btn btn-xs btn-danger" type="button" onClick={() => drop(template)}>
                  Remove
                </button>
              </div>
            </div>
            <hr className="dotted" />
          </section>
        ))}

        {page.data.templates.length === 0 ? (
          <div className="alert alert-info">
            <i className="fas fa-info-circle" aria-hidden="true"></i> No signature templates.
          </div>
        ) : null}
      </div>

      <SignatureModal
        open={adding}
        title="New user signature"
        template={null}
        variables={page.data.variables}
        onClose={() => setAdding(false)}
        onSaved={() => {
          setAdding(false)
          reload()
        }}
      />

      <SignatureModal
        open={editing !== null}
        title="Edit template"
        template={editing}
        variables={page.data.variables}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          reload()
        }}
      />

      <Modal
        id="previewModal"
        open={preview !== null}
        onClose={() => setPreview(null)}
        title="Signature body"
        labelledBy="previewModalLabel"
      >
        <textarea id="sign" className="signature-body form-control" rows={12} readOnly value={preview?.body ?? ''} />
        <div className="mt-2 aright">
          <button
            className="btn btn-default"
            type="button"
            onClick={() => {
              void navigator.clipboard
                ?.writeText(preview?.body ?? '')
                .then(() => toast('Copied', 'Success'))
                .catch(() => toast('Could not copy', 'Error', 'error'))
            }}
          >
            Copy
          </button>
        </div>
      </Modal>
    </div>
  )
}

/** Ce arata lista: primele 100 de caractere, fara etichete - ca in `list.php`. */
function plainPreview(body: string): string {
  const text = body.replace(/<[^>]*>/g, '')
  return body.length > 100 ? text.slice(0, 100) + '...' : text
}

function SignatureModal({
  open,
  title,
  template,
  variables,
  onClose,
  onSaved,
}: {
  open: boolean
  title: string
  template: SignatureTemplate | null
  variables: SignatureVariable[]
  onClose(): void
  onSaved(): void
}) {
  const [name, setName] = useState('')
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const area = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (!open) return
    setName(template?.name ?? '')
    setBody(template?.body ?? '')
  }, [open, template])

  /** Variabila se lipeste la cursor, ca in `addForm-1.js`. */
  const paste = (variable: string) => {
    const el = area.current
    if (!el) {
      setBody(body + variable)
      return
    }
    const start = el.selectionStart ?? body.length
    const end = el.selectionEnd ?? body.length
    setBody(body.slice(0, start) + variable + body.slice(end))
    window.setTimeout(() => {
      el.focus()
      el.setSelectionRange(start + variable.length, start + variable.length)
    }, 0)
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    const call =
      template === null
        ? createSignature({ template_name: name, template_body: body })
        : saveSignature(template.id, { template_name: name, template_body: body })
    void call
      .then(() => {
        toast(template === null ? 'Signature created' : 'Signature updated', 'Success')
        onSaved()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <Modal
      id={template === null ? 'newUserSignatureModal' : 'editTemplateModal'}
      open={open}
      onClose={onClose}
      title={title}
      labelledBy="signatureModalLabel"
      large
    >
      <form method="post" onSubmit={submit}>
        <div className="form-group">
          <label className="control-label">
            Name <span className="required">*</span>
          </label>
          <input
            type="text"
            className="form-control input-sm"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="control-label">Signature body</label>
          <div className="row">
            <div className="col-md-8">
              <textarea
                ref={area}
                name="template_body"
                className="signature-body form-control"
                rows={12}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
              <span className="help-block">
                HTML is allowed. Images have to be linked from somewhere else - uploading one from the editor stayed in
                the old portal.
              </span>
            </div>
            <div className="col-md-4">
              <h5>Options list</h5>
              <hr className="dotted" />
              <ul style={{ height: 220, overflowY: 'scroll', paddingLeft: 0, listStyle: 'none' }}>
                {variables.map((variable) => (
                  <li key={variable.variable}>
                    <a
                      href="#paste"
                      className="signature-option"
                      onClick={(event) => {
                        event.preventDefault()
                        paste(variable.variable)
                      }}
                    >
                      <i className="far fa-check-square" aria-hidden="true"></i> {variable.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <hr className="dotted" />
        <div className="form-group aright">
          <button type="button" className="btn btn-default" data-dismiss="modal" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {template === null ? 'Create' : 'Update'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
